import * as React from 'react';
import { cn } from '@/hen-ui/utils';
import {
  degreeCardTinyVariants,
  type DegreeCardTinyVariantProps,
} from './degree-card-tiny.variants';
import { Button, Link, Text } from '@/hen-ui/atomics';
import { Flex } from '@/hen-ui/composites';
import { CardRoot } from '@/hen-ui/composites/Card/Card';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import type { DegreeCardCtaProps } from '../DegreeCardDetailed/types';

export type DegreeCardTinyProps = React.HTMLAttributes<HTMLDivElement> &
  DegreeCardTinyVariantProps &
  DegreeCardCtaProps & {
    /** Heart icon in header. Default: false */
    favorite?: boolean;
    /** Called when the favorite (heart) button is clicked. Use with favorite=true to wire up. */
    onFavoriteClick?: () => void;
    schoolName: string;
    degreeTitle: string;
    ctaLabel: string;
  };

/**
 * DegreeCardTiny — minimal card for dense listings (10px radius).
 * School name, degree title, and CTA link. Width is fluid.
 * Reuse: Card, Button, Link, Text.
 */
export const DegreeCardTiny = React.forwardRef<
  HTMLDivElement,
  DegreeCardTinyProps
>(
  (
    {
      favorite = false,
      onFavoriteClick,
      schoolName,
      degreeTitle,
      ctaLabel,
      ctaHref,
      onCtaClick,
      className,
      border,
      ...props
    },
    ref,
  ) => {
    return (
      <CardRoot
        ref={ref}
        className={cn(degreeCardTinyVariants({ border }), className)}
        data-hen-id={COMPONENT_IDS.DegreeCardTiny}
        {...props}
      >
        <Flex direction='column' gap='xxs' width='full' className='p-b4'>
          <Flex direction='row' align='start' justify='between' gap='xs'>
            <Text variant='l1' weight='bold' color='subtext'>
              {schoolName}
            </Text>
            {favorite && (
              <Button
                variant='iconOnly'
                size='sm'
                iconBefore='heart'
                aria-label='Add to favorites'
                onClick={onFavoriteClick}
              />
            )}
          </Flex>
          <Text variant='b5' tag='span' weight='bold' color='heading'>
            {degreeTitle}
          </Text>
          {ctaHref?.trim() ? (
            <Link
              href={ctaHref}
              variant='primary'
              size='sm'
              weight='regular'
              onClick={onCtaClick}
            >
              {ctaLabel}
            </Link>
          ) : onCtaClick ? (
            <Button
              type='button'
              variant='textOnly'
              size='flush'
              onClick={onCtaClick}
            >
              {ctaLabel}
            </Button>
          ) : null}
        </Flex>
      </CardRoot>
    );
  },
);

DegreeCardTiny.displayName = 'DegreeCardTiny';
